import type {
  AccountVerificationRequest,
  AccountVerificationResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  LoginRequest,
  LoginResponse,
  LoginOtpVerificationRequest,
  LoginOtpVerificationResponse,
  OtpVerificationRequest,
  OtpVerificationResponse,
  RefreshTokenResponse,
  ResendOtpRequest,
  ResendOtpResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SignupRequest,
  SignupResponse,
} from '../types'
import { api, resetLogoutFlag } from './api'
import { startTokenRefresh, stopTokenRefresh } from './tokenRefreshService'

export class AuthService {
  /**
   * Register a new user
   */
  static async signup(data: SignupRequest): Promise<SignupResponse> {
    return await api.post<SignupResponse['response']>('/auth/signup', data)
  }

  /**
   * Login user
   */
  static async login(data: LoginRequest): Promise<LoginResponse> {
    return await api.post<LoginResponse['response']>('/auth/login', data)
  }

  /**
   * Verify account with OTP
   */
  static async verifyAccount(
    data: AccountVerificationRequest
  ): Promise<AccountVerificationResponse> {
    return await api.post<AccountVerificationResponse['response']>(
      '/auth/account-verification',
      data
    )
  }

  /**
   * Request password reset
   */
  static async forgotPassword(
    data: ForgotPasswordRequest
  ): Promise<ForgotPasswordResponse> {
    return await api.post<ForgotPasswordResponse['response']>(
      '/auth/forgot-password',
      data
    )
  }

  /**
   * Reset password with OTP
   */
  static async resetPassword(
    data: ResetPasswordRequest
  ): Promise<ResetPasswordResponse> {
    return await api.post<ResetPasswordResponse['response']>(
      '/auth/reset-password',
      data
    )
  }

  /**
   * Resend OTP
   */
  static async resendOtp(data: ResendOtpRequest): Promise<ResendOtpResponse> {
    return await api.post<ResendOtpResponse['response']>(
      '/auth/resend-otp',
      data
    )
  }

  /**
   * Verify OTP for login (2FA - Step 2)
   * This is the new endpoint for the 2FA login flow
   */
  static async loginVerifyOtp(
    data: LoginOtpVerificationRequest
  ): Promise<LoginOtpVerificationResponse> {
    return await api.post<LoginOtpVerificationResponse['response']>(
      '/auth/login/verify-otp',
      data
    )
  }

  /**
   * Verify OTP for login (deprecated - use loginVerifyOtp instead)
   * Kept for backward compatibility
   */
  static async verifyOtp(
    data: OtpVerificationRequest
  ): Promise<OtpVerificationResponse> {
    return await api.post<OtpVerificationResponse['response']>(
      '/auth/verify-otp',
      data
    )
  }

  /**
   * Refresh access token using refresh token
   */
  static async refreshToken(
    refreshToken: string
  ): Promise<RefreshTokenResponse> {
    return await api.post<RefreshTokenResponse['response']>(
      '/auth/refresh-token',
      { refreshToken }
    )
  }

  /**
   * Store authentication tokens and start automatic token refresh
   */
  static setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('accessToken', accessToken)
    localStorage.setItem('refreshToken', refreshToken)
    // Reset logout flag when tokens are set (user logged in successfully)
    resetLogoutFlag()
    // Start automatic token refresh
    startTokenRefresh()
  }

  /**
   * Get stored access token
   */
  static getAccessToken(): string | null {
    return localStorage.getItem('accessToken')
  }

  /**
   * Get stored refresh token
   */
  static getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken')
  }

  /**
   * Clear stored tokens
   */
  static clearTokens(): void {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    return !!this.getAccessToken()
  }

  /**
   * Logout user - clears tokens and redirects to login
   * @param delay - Optional delay in milliseconds before redirect (useful for showing toast messages)
   */
  static logout(delay: number = 0): void {
    // Stop automatic token refresh
    stopTokenRefresh()
    
    // Clear tokens first
    this.clearTokens()
    // Clear any other stored auth-related data
    localStorage.removeItem('loginEmail')
    localStorage.removeItem('verificationEmail')
    
    // Clear sessionStorage items (HMRC redirect, callback tracking, etc.)
    sessionStorage.removeItem('hmrcAuthRedirect')
    // Clear any HMRC callback tracking keys
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('hmrc_callback_')) {
        sessionStorage.removeItem(key)
      }
    })
    
    // Clear Companies House session
    try {
      // Dynamic import to avoid circular dependencies
      import('./companiesHouseSessionService').then(({ companiesHouseSessionService }) => {
        companiesHouseSessionService.clearSession()
      }).catch((error) => {
        // Ignore if module not available
        console.warn('Could not clear Companies House session:', error)
      })
    } catch (error) {
      // Ignore if module not available
      console.warn('Could not clear Companies House session:', error)
    }
    
    // Use replace to prevent back button navigation
    // Redirect to login page after delay (allows toast to be visible)
    if (delay > 0) {
      setTimeout(() => {
        window.location.replace('/login')
      }, delay)
    } else {
      window.location.replace('/login')
    }
  }
}
